import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, AlertCircle, CheckCircle, Loader2, Globe } from 'lucide-react';
import VideoJSPlayer from './VideoJSPlayer';
import PaymentSection from './PaymentSection';
import { LanguageSelectionModal } from './LanguageSelectionModal';
import { translateSrt } from '@/utils/apiWithRecaptcha';
import { useRecaptcha } from '@/contexts/RecaptchaContext';
import { useToast } from '@/hooks/use-toast';



interface VideoPreviewProps {
  videoId: string;
  jobId: string;
  srtContent?: string;
  srtUrl?: string;
  vttUrl?: string; // 🚀 NEW: VTT URL for VideoJS
  videoUrl?: string;
  translatedSrtUrl?: string;
  translatedVttUrl?: string; // 🚀 NEW: Translated VTT URL
  fallbackVideoUrl?: string; // 🚀 NEW: fallback S3 URL if Object URL fails
  onStartFullProcessing?: () => void;
  videoData?: any;
  startFull?: () => Promise<void>;
  onReset?: () => void; // NEW: callback to reset and start over
  isStartingFull?: boolean; // NEW: loading state from parent
  uploadState?: any; // NEW: check uploadState from parent for full URLs
  hasClickedFullProcessing?: boolean; // NEW: track if user has clicked full processing button

  onPaymentComplete?: (jobId: string) => void; // NEW: callback when payment is completed with jobId
}

// 🚀 TypeScript refresh trigger - fallbackVideoUrl added

export default function VideoPreview({
  videoId,
  jobId,
  srtContent,
  srtUrl,
  vttUrl, // 🚀 NEW: VTT URL for VideoJS
  videoUrl,
  translatedSrtUrl,
  translatedVttUrl, // 🚀 NEW
  fallbackVideoUrl,
  onStartFullProcessing,
  videoData,
  startFull,
  onReset,
  isStartingFull: parentIsStartingFull,
  uploadState,
  onPaymentComplete
}: VideoPreviewProps) {
  const { t } = useTranslation();
  const [subtitles, setSubtitles] = useState<string>('');
  const [isLoadingSubtitles, setIsLoadingSubtitles] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showStartFromBeginModal, setShowStartFromBeginModal] = useState(false);
  
  // 🚀 NEW: Translation state
  const [showLanguageModal, setShowLanguageModal] = useState<boolean>(false);
  const [isTranslating, setIsTranslating] = useState<boolean>(false);
  const [selectedTargetLanguage, setSelectedTargetLanguage] = useState<string>('0');
  const [selectedSourceLanguage, setSelectedSourceLanguage] = useState<string>('auto');
  const [hasShownLanguageModal, setHasShownLanguageModal] = useState<boolean>(false);
  
  // 🚀 Improved loading UI state
  const [translateProgress, setTranslateProgress] = useState<number>(0);
  const [tipIndex, setTipIndex] = useState<number>(0);
  const loadingTips = [
    'Đang chia nhỏ phụ đề thành các đoạn...',
    'Đang dịch kèm ngữ cảnh hội thoại...',
    'Đang ghép các câu dịch...',
    'Đang tối ưu hiển thị cho phụ đề...',
    'Chuẩn bị tải phụ đề đã dịch...'
  ];
  
  useEffect(() => {
    if (!isTranslating) {
      setTipIndex(0);
      setTranslateProgress(prev => (prev > 0 ? 100 : 0));
      return;
    }
    setTranslateProgress(0);
    let progress = 0;
    const progressTimer = setInterval(() => {
      const increment = progress < 70 ? Math.random() * 7 + 3 : progress < 90 ? Math.random() * 3 + 1 : Math.random() * 1;
      progress = Math.min(progress + increment, 95);
      setTranslateProgress(Math.round(progress));
    }, 400);
    const tipTimer = setInterval(() => {
      setTipIndex(prev => (prev + 1) % loadingTips.length);
    }, 1800);
    return () => { clearInterval(progressTimer); clearInterval(tipTimer); };
  }, [isTranslating]);
  
  const { executeRecaptcha } = useRecaptcha();
  const { toast } = useToast();

  // 🚀 NEW: Ref for language section to enable auto-scroll
  const languageSectionRef = useRef<HTMLDivElement>(null);
  
  // 🚀 NEW: Ref for video player to scroll after translation
  const videoPlayerRef = useRef<HTMLDivElement>(null);

  // 🚀 NEW: Function to scroll to language section
  const scrollToLanguageSection = useCallback(() => {
    console.log('📜 VideoPreview - Attempting to scroll to language section...');
    if (languageSectionRef.current) {
      const elementTop = languageSectionRef.current.offsetTop;
      const offset = -400; // Increased offset for more noticeable scroll
      
      console.log('📜 VideoPreview - Scrolling to position:', elementTop - offset);
      window.scrollTo({
        top: elementTop - offset,
        behavior: 'smooth'
      });
      
      console.log('📜 VideoPreview - Scrolled to language section for translation visibility');
    } else {
      console.warn('⚠️ VideoPreview - languageSectionRef.current is null, cannot scroll');
    }
  }, []);

  // 🚀 NEW: Function to scroll to video player after translation
  const scrollToVideoPlayer = useCallback(() => {
    console.log('📜 VideoPreview - Attempting to scroll to video player...');
    if (videoPlayerRef.current) {
      const elementTop = videoPlayerRef.current.offsetTop;
      const offset =-400; // Small offset to show video player nicely
      
      console.log('📜 VideoPreview - Scrolling to video player position:', elementTop - offset);
      window.scrollTo({
        top: elementTop - offset,
        behavior: 'smooth'
      });
      
      console.log('📜 VideoPreview - Scrolled to video player for translated subtitles viewing');
    } else {
      console.warn('⚠️ VideoPreview - videoPlayerRef.current is null, cannot scroll');
    }
  }, []);

  // 🚀 NEW: Translation limit state
  const [translationCount, setTranslationCount] = useState<number>(0);
  const [isTranslationLimitReached, setIsTranslationLimitReached] = useState<boolean>(false);
  
  // 🚀 NEW: Current translated subtitle URLs
  const [currentTranslatedSrtUrl, setCurrentTranslatedSrtUrl] = useState<string | null>(translatedSrtUrl || null);
  const [currentTranslatedVttUrl, setCurrentTranslatedVttUrl] = useState<string | null>(translatedVttUrl || null);

  // Use parent's loading state directly
  const isStartingFull = parentIsStartingFull || false;

  // 🚀 NEW: Check translation limit on component mount
  useEffect(() => {
    if (videoData?.preview?.translation_count !== undefined) {
      setTranslationCount(videoData.preview.translation_count);
      setIsTranslationLimitReached(videoData.preview.translation_count >= 9);
    }
  }, [videoData?.preview?.translation_count]);

  // 🚀 NEW: Show language modal when processing is complete
  useEffect(() => {
    const isPreviewCompleted = videoData?.preview?.status === 'completed';
    const isFullCompleted = videoData?.full?.status === 'completed';
    
    // Show language modal when preview is completed and we haven't shown it yet
    if (isPreviewCompleted && !hasShownLanguageModal && !isTranslationLimitReached) {
      console.log('🌐 VideoPreview - Processing complete, showing language modal');
      setShowLanguageModal(true);
      setHasShownLanguageModal(true);
    }
  }, [videoData?.preview?.status, videoData?.full?.status, hasShownLanguageModal, isTranslationLimitReached]);

  // 🚀 DEBUG: Monitor modal state changes
  useEffect(() => {
    console.log('🌐 VideoPreview - showLanguageModal changed:', showLanguageModal);
  }, [showLanguageModal]);

  // 🚀 NEW: Monitor translatedSrtUrl changes from parent
  useEffect(() => {
    if (translatedSrtUrl && translatedSrtUrl !== currentTranslatedSrtUrl) {
      console.log('🌐 VideoPreview - New translated SRT URL detected from parent:', translatedSrtUrl);
      setCurrentTranslatedSrtUrl(translatedSrtUrl);
    }
  }, [translatedSrtUrl, currentTranslatedSrtUrl]);

  // 🚀 NEW: Monitor translatedVttUrl changes from parent
  useEffect(() => {
    if (translatedVttUrl && translatedVttUrl !== currentTranslatedVttUrl) {
      console.log('🌐 VideoPreview - New translated VTT URL detected from parent:', translatedVttUrl);
      setCurrentTranslatedVttUrl(translatedVttUrl);
    }
  }, [translatedVttUrl, currentTranslatedVttUrl]);

  // 🚀 NEW: Monitor currentTranslatedSrtUrl changes and force JWPlayer update
  useEffect(() => {
    console.log('🌐 VideoPreview - currentTranslatedSrtUrl changed:', currentTranslatedSrtUrl);
    if (currentTranslatedSrtUrl) {
      console.log('🌐 VideoPreview - JWPlayer should update tracks with new translated subtitle');
      // This will trigger the useEffect in JWPlayer to reload with new subtitle tracks
    }
  }, [currentTranslatedSrtUrl]);




  // Load subtitles from content, URL, or videoData
  useEffect(() => {
    console.log('🔄 VideoPreview - useEffect triggered for subtitles');
    console.log('🔄 VideoPreview - Dependencies:', {
      srtContent: !!srtContent,
      srtUrl,
      videoDataPreviewSrt: videoData?.status?.previewUrls?.srt,
      translatedSrtUrl
    });
    
    // Priority: translated SRT > srtContent > videoData preview > srtUrl
    if (translatedSrtUrl) {
      console.log('🔄 VideoPreview - Loading translated SRT');
      loadSubtitlesFromUrl(translatedSrtUrl);
    } else if (srtContent) {
      console.log('🔄 VideoPreview - Using srtContent');
      setSubtitles(srtContent);
    } else if (srtUrl) {
      console.log('🔄 VideoPreview - Loading from srtUrl');
      loadSubtitlesFromUrl(srtUrl);
    } else if (videoData?.status?.previewUrls?.srt) {
      console.log('🔄 VideoPreview - Loading from videoData preview');
      loadSubtitlesFromUrl(videoData.status.previewUrls.srt);
    }
  }, [srtContent, srtUrl, translatedSrtUrl]); // Remove videoData dependency to prevent infinite loop

  // Debounce subtitle loading to prevent excessive API calls
  const [lastLoadUrl, setLastLoadUrl] = useState<string>('');
  
  const loadSubtitlesFromUrl = useCallback(async (url: string) => {
    // Prevent loading the same URL multiple times
    if (lastLoadUrl === url) {
      console.log('🔄 VideoPreview - Skipping duplicate URL load:', url);
      return;
    }
    
    console.log('🔄 VideoPreview - Loading subtitles from:', url);
    setLastLoadUrl(url);
    setIsLoadingSubtitles(true);
    setError(null);
    
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to load subtitles: ${response.statusText}`);
      }
      
      const content = await response.text();
      setSubtitles(content);
      console.log('✅ VideoPreview - Subtitles loaded successfully');
    } catch (err) {
      console.error('❌ VideoPreview - Error loading subtitles:', err);
      setError(err instanceof Error ? err.message : 'Failed to load subtitles');
    } finally {
      setIsLoadingSubtitles(false);
    }
  }, [lastLoadUrl]);



  // 🚀 NEW: Translation functions
  const openLanguageModal = () => {
    console.log('🌐 VideoPreview - openLanguageModal called');
    console.log('🌐 VideoPreview - isTranslationLimitReached:', isTranslationLimitReached);
    
    if (isTranslationLimitReached) {
      toast({
        title: "Translation limit reached",
        description: "You have reached the maximum of 3 translations for this video.",
        variant: "destructive",
      });
      return;
    }
    
    console.log('🌐 VideoPreview - Setting showLanguageModal to true');
    setShowLanguageModal(true);
  };

  // 🚀 FIXED: Language selection logic - pass language directly to avoid state timing issues
  const handleLanguageModalConfirm = async (selectedLanguage: string | null) => {
    console.log('🌐 VideoPreview - Language modal confirmed:', { selectedLanguage });
    
    if (!selectedLanguage || selectedLanguage === '0') {
      console.log('⚠️ VideoPreview - No language selected, closing modal');
      setShowLanguageModal(false);
      return;
    }

    console.log('✅ VideoPreview - Setting selected language:', selectedLanguage);
    setSelectedTargetLanguage(selectedLanguage);
    setShowLanguageModal(false);
    
    // 🚀 FIX: Wait for state update before starting translation
    console.log('🌐 VideoPreview - Starting translation with language:', selectedLanguage);
    await startTranslation(selectedLanguage); // Pass language directly
  };

  const startTranslation = async (targetLanguage: string) => {
    if (!videoData?.preview?.s3_srt_url) {
      toast({
        title: "No subtitles available",
        description: "Please wait for subtitles to be generated before translating.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsTranslating(true);
      console.log('🌐 VideoPreview - Starting translation to:', targetLanguage);
      
      // 🚀 NEW: Auto-scroll to language section to show loading state
      setTimeout(() => {
        scrollToLanguageSection();
      }, 100); // Small delay to ensure element is rendered

      const response = await translateSrt(
        videoData.preview.s3_srt_url,
        targetLanguage,
        'auto',
        videoId,
        executeRecaptcha
      );

      if (response.translated_srt_url) {
        console.log('✅ VideoPreview - Translation completed successfully');
        console.log('🌐 VideoPreview - New translated SRT URL:', response.translated_srt_url);
        
        toast({
          title: "Translation completed!",
          description: "Your subtitles have been translated with context-aware technology for better movie dialogue quality. The player will now show the translated version.",
        });
        
        // Update translation count
        setTranslationCount(prev => {
          const newCount = prev + 1;
          setIsTranslationLimitReached(newCount >= 3);
          return newCount;
        });
        
        // Update current translated SRT URL to show in JWPlayer
        console.log('🌐 VideoPreview - Setting currentTranslatedSrtUrl to:', response.translated_srt_url);
        setCurrentTranslatedSrtUrl(response.translated_srt_url);

        // 🚀 NEW: lấy VTT từ API hoặc sinh từ SRT
        let vttFromApi = (response as any).translated_vtt_url as string | undefined;
        if (vttFromApi && !/\.vtt(\?|$)/i.test(vttFromApi)) {
          // phòng trường hợp BE trả URL kèm đuôi khác
          vttFromApi = undefined;
        }
        if (!vttFromApi && response.translated_srt_url) {
          vttFromApi = response.translated_srt_url
            .replace('/translated-srt/', '/translated-vtt/')
            .replace(/\.srt(\?.*)?$/i, '.vtt$1');
        }
        console.log('🌐 VideoPreview - Setting currentTranslatedVttUrl to:', vttFromApi);
        setCurrentTranslatedVttUrl(vttFromApi ?? null);
        
        // Close modal and show success message
        setShowLanguageModal(false);
        
        // Show success message in UI
        setError(null); // Clear any previous errors
        
        // 🚀 FIX: Force JWPlayer to reload with new subtitle tracks
        // This will trigger the useEffect in JWPlayer to update tracks and set translated as default
        console.log('🌐 VideoPreview - Translation completed, JWPlayer will reload with translated subtitles');
        
        // Force a small delay to ensure state is updated before JWPlayer useEffect triggers
        setTimeout(() => {
          console.log('🌐 VideoPreview - Delayed check - currentTranslatedSrtUrl:', currentTranslatedSrtUrl);
          console.log('🌐 VideoPreview - JWPlayer should have received new translatedSrtUrl prop');
          
          // 🚀 NEW: Scroll to video player after translation completes
          scrollToVideoPlayer();
        }, 500); // Increased delay to ensure translation UI updates first
        
        // Note: Translation is now complete and JWPlayer will show translated subtitles
      } else {
        throw new Error('Translation failed - no URL returned');
      }
    } catch (error) {
      console.error('❌ VideoPreview - Translation error:', error);
      toast({
        title: "Translation failed",
        description: error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsTranslating(false);
    }
  };

  const handleStartFullProcessing = async () => {
    if (isStartingFull) return; // Prevent double-click
    
    // Gọi parent callback ngay lập tức để UI đổi state
    // Parent sẽ set isStartingFull=true + status='full_processing' và tự gọi startFull()
    if (onStartFullProcessing) {
      onStartFullProcessing();
    }
  };

  const renderStatusBadge = () => {
    if (!videoData) return null;

    const { status } = videoData.status;
    
    switch (status) {
      case 'uploading':
        return (
          <div className="flex items-center space-x-2 text-blue-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Processing Preview...</span>
          </div>
        );
      
      case 'preview_ready':
        return (
          <div className="flex items-center space-x-2 text-green-600">
            <CheckCircle className="h-4 w-4" />
            <span>Preview Ready!</span>
          </div>
        );
      

      
      case 'full_processing':
        return (
          <div className="flex items-center space-x-2 text-blue-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Processing Full Video...</span>
          </div>
        );
      
      case 'full_ready':
        return (
          <div className="flex items-center space-x-2 text-green-600">
            <CheckCircle className="h-4 w-4" />
            <span>Full Video Ready!</span>
          </div>
        );
      
      case 'error':
        return (
          <div className="flex items-center space-x-2 text-red-600">
            <AlertCircle className="h-4 w-4" />
            <span>Error: {videoData.status.error}</span>
          </div>
        );
      
      default:
        return null;
    }
  };





  const renderActionButtons = () => {
    console.log('🎬 VideoPreview - renderActionButtons - videoData:', videoData);
    console.log('🎬 VideoPreview - renderActionButtons - videoData?.preview?.status:', videoData?.preview?.status);
    console.log('🎬 VideoPreview - renderActionButtons - videoData?.full?.status:', videoData?.full?.status);
    
    if (!videoData) return null;

    // 🚀 FIX: Logic đơn giản - chỉ check videoData status
    const isPreviewCompleted = videoData?.preview?.status === 'completed';
    const isFullCompleted = videoData?.full?.status === 'completed';
    const isFullProcessing = videoData?.full?.status === 'processing';
    
    console.log('🎬 VideoPreview - renderActionButtons - Status check:', {
      isPreviewCompleted,
      isFullCompleted,
      isFullProcessing
    });
    
    // 🚀 FIX: Show "Start from Beginning" chỉ khi full thực sự completed
    if (isFullCompleted) {
      console.log('🎬 VideoPreview - renderActionButtons - Rendering "Start from Beginning" button for full video ready');
      return (
        <div className="mt-4">
          <button
            onClick={onReset}
            className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 hover:shadow-md active:scale-95 transition-all duration-200 font-semibold"
            aria-label="Start from beginning"
          >
            🔄 Start from Beginning
          </button>
        </div>
      );
    }
    
    // 🚀 FIX: Show payment button khi preview completed và full chưa start
    if (isPreviewCompleted && !isFullCompleted && !isFullProcessing) {
      console.log('🎬 VideoPreview - renderActionButtons - Rendering payment button for completed preview');
      return (
        <div className="mt-4 mr-4 ml-4">
          <button
            onClick={() => {
              // 🚀 Fix: Scroll to PaymentSection when button clicked
              if (onPaymentComplete) {
                console.log('💰 Process Full Video clicked - scrolling to PaymentSection');
                
                // Scroll to PaymentSection
                const paymentSection = document.querySelector('[data-payment-section]');
                if (paymentSection) {
                  const elementTop = paymentSection.getBoundingClientRect().top + window.scrollY;
                  window.scrollTo({
                    top: elementTop + 300,
                    behavior: 'smooth'
                  });
                  console.log('✅ Scrolled to PaymentSection');
                } else {
                  console.log('⚠️ PaymentSection not found, scrolling to bottom');
                  window.scrollTo({ 
                    top: document.body.scrollHeight, 
                    behavior: 'smooth' 
                  });
                }
              }
            }}
            disabled={!onPaymentComplete}
            className={`w-full px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg ${
              onPaymentComplete
                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white hover:shadow-xl transform hover:scale-105 hover:-translate-y-1 active:scale-95 ring-2 ring-blue-500/20 hover:ring-blue-500/40'
                : 'bg-gray-400 text-gray-600 cursor-not-allowed'
            }`}
            aria-label="Process full video with payment"
          >
            💳 Process Full Video (Payment Required)
          </button>
        </div>
      );
    }

    // 🚀 FIX: Show processing status khi full đang xử lý
    if (isFullProcessing) {
      return (
        <div className="mt-4">
          <div className="flex items-center justify-center space-x-2 text-blue-600">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="font-medium">Processing full video...</span>
                  </div>

        {/* 🚀 NEW: Language Selection Modal */}
        <LanguageSelectionModal
          isOpen={showLanguageModal}
          onClose={() => setShowLanguageModal(false)}
          onConfirm={handleLanguageModalConfirm}
          fileName={videoData?.original_name || 'video'}
        />
      </div>
    );
  }

    return null;
  };

  // Logic: Nếu có translate thì đợi, không có thì mount ngay
  const shouldShowPlayer = !currentTranslatedSrtUrl || (currentTranslatedSrtUrl && !isTranslating);
  
  // 🚀 Fix: Priority for preview - translated SRT > preview SRT > original SRT
  const effectiveSrtUrl = currentTranslatedSrtUrl || 
                          videoData?.preview?.s3_srt_url ||  // 🚀 Preview SRT first
                          srtUrl;                                  // Original SRT fallback
  
  // 🚀 NEW: Clear distinction between original and translated subtitles
  const originalSrtUrl = videoData?.preview?.s3_srt_url || srtUrl; // Original SRT (preview or fallback)
  const translatedSrtUrlFinal = currentTranslatedSrtUrl; // Translated SRT (if available)
  
  console.log('🎬 VideoPreview - Player Logic (Fixed Priority):', {
    translatedSrtUrl: !!currentTranslatedSrtUrl,
    previewSrt: videoData?.preview?.s3_srt_url,
    originalSrt: srtUrl,
    isTranslating,
    shouldShowPlayer,
    effectiveSrtUrl,
    originalSrtUrl,
    translatedSrtUrlFinal
  });

  return (
    <div className="w-full video-preview-full-width">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Video Preview
            {currentTranslatedSrtUrl && (
              <span className="ml-2 text-sm text-green-600 bg-green-100 px-2 py-1 rounded">
                Translated Subtitles
              </span>
            )}
          </h3>
        </div>
        
        <div ref={videoPlayerRef} className="relative">
          {shouldShowPlayer ? (
            <>
              {console.log('🎬 VideoPreview - Rendering VideoJSPlayer with props:', {
                videoUrl: videoUrl || '',
                srtUrl: effectiveSrtUrl,
                originalSrtUrl,
                translatedSrtUrl: currentTranslatedSrtUrl || undefined,
                fallbackVideoUrl,
                shouldShowPlayer
              })}
              <VideoJSPlayer
                key={`videojs-${videoId}-${videoUrl}`}
                videoUrl={videoUrl || ''}
                srtUrl={effectiveSrtUrl}
                vttUrl={vttUrl} // 🚀 NEW: VTT URL for VideoJS
                originalSrtUrl={originalSrtUrl}
                translatedSrtUrl={currentTranslatedSrtUrl || undefined}
                translatedVttUrl={currentTranslatedVttUrl || undefined} // 🚀 NEW: Translated VTT URL
                fallbackVideoUrl={fallbackVideoUrl} // 🚀 NEW: S3 fallback URL
                onReady={() => console.log('🎬 VideoJSPlayer ready')}
                onError={(error: any) => console.error('❌ VideoJSPlayer error:', error)}
              />
            </>
          ) : (
            <div className="w-full h-[18rem] md:h-[32rem] bg-gray-100 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 font-medium">Translating subtitles...</p>
                <p className="text-sm text-gray-500">Please wait while we translate your subtitles</p>
              </div>
            </div>
          )}
        </div>
        
        {effectiveSrtUrl && (
          <div className="p-4 bg-gray-50 border-t border-gray-200">
            <div className="flex flex-col space-y-3">
              {/* 🚀 Process Full Video button - FIRST */}
              {onPaymentComplete && videoData && (
                <div className="text-center pb-3 border-b border-gray-200">
                  <button
                    onClick={() => {
                      console.log('💰 Process Full Video clicked - scrolling to PaymentSection');
                      
                      // Scroll to PaymentSection
                      const paymentSection = document.querySelector('[data-payment-section]');
                      if (paymentSection) {
                        const elementTop = paymentSection.getBoundingClientRect().top + window.scrollY;
                        window.scrollTo({
                          top: elementTop + 300,
                          behavior: 'smooth'
                        });
                        console.log('✅ Scrolled to PaymentSection');
                      } else {
                        console.log('⚠️ PaymentSection not found, scrolling to bottom');
                        window.scrollTo({ 
                          top: document.body.scrollHeight, 
                          behavior: 'smooth' 
                        });
                      }
                    }}
                    disabled={false}
                    className="w-full px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white hover:shadow-xl transform hover:scale-105 hover:-translate-y-1 active:scale-95 ring-2 ring-blue-500/20 hover:ring-blue-500/40"
                    aria-label="Process full video with payment"
                  >
                    💳 Process Full Video (Payment Required)
                  </button>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                {/* Original SRT Button */}
                {srtUrl && (
                  <button
                    onClick={() => window.open(srtUrl, '_blank')}
                    className="flex-1 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg border border-blue-300 transition-colors font-medium text-sm"
                  >
                    📄 Download Original SRT
                  </button>
                )}
                
                {/* Original VTT Button */}
                {(videoData?.preview?.s3_vtt_url || videoData?.full?.s3_vtt_url) && (
                  <button
                    onClick={() => window.open(videoData?.preview?.s3_vtt_url || videoData?.full?.s3_vtt_url, '_blank')}
                    className="flex-1 px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg border border-purple-300 transition-colors font-medium text-sm"
                  >
                    📄 Download Original VTT
                  </button>
                )}
                
                {/* Translated SRT Button */}
                {currentTranslatedSrtUrl && (
                  <button
                    onClick={() => window.open(currentTranslatedSrtUrl, '_blank')}
                    className="flex-1 px-4 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg border border-green-300 transition-colors font-medium text-sm"
                  >
                    🌐 Download Translated SRT
                  </button>
                )}
                
                {/* Translated VTT Button */}
                {(() => {
                  console.log('🔍 VideoPreview - VTT Button Debug:', {
                    currentTranslatedVttUrl,
                    translatedVttUrl,
                    shouldShow: !!currentTranslatedVttUrl
                  });
                  return null;
                })()}
                {currentTranslatedVttUrl && (
                  <button
                    onClick={() => window.open(currentTranslatedVttUrl, '_blank')}
                    className="flex-1 px-4 py-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg border border-indigo-300 transition-colors font-medium text-sm"
                  >
                    🌐 Download Translated VTT
                  </button>
                )}
              </div>

              {/* 🚀 NEW: Translation Service */}
              <div ref={languageSectionRef} className="mt-4 border border-gray-200 rounded-lg p-6 bg-gradient-to-r from-blue-50 to-indigo-50 w-full max-w-2xl mx-auto">
                <h4 className="font-bold text-gray-900 mb-3 text-lg">🌐 Translation Service</h4>
                <p className="text-base text-gray-700 mb-4">
                  Translate your subtitles to another language
                </p>

                {isTranslating && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-blue-700 font-medium">🔄 Đang dịch phụ đề...</p>
                      <span className="text-xs text-blue-700">{translateProgress}%</span>
                    </div>
                    <div className="w-full h-2 bg-blue-100 rounded-full overflow-hidden">
                      <div className="h-2 bg-gradient-to-r from-blue-500 to-indigo-500" style={{ width: `${translateProgress}%` }} />
                    </div>
                    <p className="text-[11px] text-blue-600 mt-2">{loadingTips[tipIndex]}</p>
                  </div>
                )}
                {currentTranslatedSrtUrl && !isTranslating && (
                  <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded text-center">
                    <p className="text-xs text-green-700">
                      ✅ Translation completed! Player now shows translated subtitles
                    </p>
                  </div>
                )}
                {!isTranslationLimitReached ? (
                  <>
                    <button
                      onClick={openLanguageModal}
                      disabled={isTranslating}
                      className="w-full px-8 py-6 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xl font-bold rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105 hover:-translate-y-1 active:scale-95 ring-2 ring-orange-500/20 hover:ring-orange-500/40"
                    >
                      {isTranslating ? (
                        <>
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-4 inline"></div>
                          <span className="text-xl">Translating...</span>
                        </>
                      ) : (
                        <>
                          <Globe className="h-8 w-8 mr-4 inline" />
                          <span className="text-xl">🌐 Translate to Another Language</span>
                        </>
                      )}
                    </button>
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      {9 - translationCount} translations remaining
                    </p>
                  </>
                ) : (
                  <div className="text-center">
                    <p className="text-sm text-red-600 font-medium mb-2">
                      ⚠️ Translation limit reached (9/9)
                    </p>
                    <button
                      onClick={() => window.location.reload()}
                      className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
                    >
                      🔄 Start Over with New File
                    </button>
                  </div>
                )}
              </div>

              {/* 🚀 Start with another file button - BELOW Translation Service */}
              <div className="text-center pt-3 border-t border-gray-200">
                <button
                  onClick={() => setShowStartFromBeginModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
                >
                  🚀 Start with another file
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  Upload and process a different video or audio file
                </p>
              </div>

            </div>
          </div>
        )}
        
        {/* Action Buttons */}
        {(() => {
          console.log('🎬 VideoPreview - About to render action buttons');
          const buttons = renderActionButtons();
          console.log('🎬 VideoPreview - Action buttons result:', buttons);
          return buttons;
        })()}


        
        {/* Payment Section */}
        {onPaymentComplete && videoData && (
          <div className="mt-6" data-payment-section>
            <PaymentSection
              videoId={videoId}
              jobId={videoData.jobId} // 🚀 Pass jobId for useVideoStatus hook
              onPaymentComplete={onPaymentComplete}
              onShowLoginModal={() => {}} // no-op
            />
          </div>
        )}
      </div>

      {/* Modal xác nhận start from begin */}
      <div className={`fixed inset-0 bg-black bg-opacity-50 z-50 ${showStartFromBeginModal ? 'block' : 'hidden'}`}>
        <div className="absolute top-1/4 left-1/2 transform -translate-x-1/2 -translate-y-[calc(50%+420px)] md:-translate-y-[calc(50%-150px)] bg-white rounded-lg p-6 w-[calc(90vw-32px)] max-w-2xl shadow-xl">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div>
              <h3 className="text-xl md:text-lg font-semibold text-gray-900">Start from beginning?</h3>
              <p className="text-base md:text-sm text-gray-600">This will reset your current progress</p>
            </div>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
              <span className="text-base md:text-sm text-yellow-800 font-medium">
                Warning: Starting over will reset all progress.
              </span>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={() => setShowStartFromBeginModal(false)}
              className="flex-1 px-4 py-3 md:py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-base md:text-sm"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setShowStartFromBeginModal(false);
                window.location.href = '/';
              }}
              className="flex-1 px-4 py-3 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-base md:text-sm"
            >
              Yes, Start Over
            </button>
          </div>
        </div>
      </div>

      {/* 🚀 NEW: Language Selection Modal */}
      <LanguageSelectionModal
        isOpen={showLanguageModal}
        onClose={() => setShowLanguageModal(false)}
        onConfirm={handleLanguageModalConfirm}
        fileName={videoData?.original_name || 'video'}
        onClearFile={() => {}} // No-op since we don't have file input in preview
      />
    </div>
  );
}
